import { useNavigate } from 'react-router-dom'

import {
  AvatarStack,
  Box,
  Card,
  InfoDot,
  ProgressBar,
  Text,
} from '@factorialco/deprecated-design-system'
import { BudgetFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { budgetStatusText } from 'modules/finance/lib/budgetTagInfo'
import {
  amountWithCurrency,
  avatarsForOwners,
  budgetPeriodicity,
  budgetProgress,
} from 'modules/finance/lib/budgets'
import { events } from 'modules/finance/lib/events'

export const Item = ({ budget }: { budget: BudgetFragment }) => {
  const {
    access: { locale },
  } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const progress = budgetProgress(budget.currentStatus)

  const overBudget = budget.currentStatus === 'over_budget'
  const availableAmount = overBudget
    ? budget.spentAmountCents - budget.amountCents
    : budget.availableAmountCents

  const statusText = budget.started
    ? budgetStatusText(budget.currentStatus)
    : i18n.t('budgets.status.not_started')

  const avatarsLimit = 4
  const owners = avatarsForOwners(budget.ownerEmployees)
  const avatars = owners.slice(0, avatarsLimit)
  const leftover = owners.length - avatarsLimit

  return (
    <Box gridColSpan={{ xxs: 12, s: 6, m: 4 }} key={budget.id}>
      <Card
        onClick={() => {
          track(events.goToBudgetDetails, { budgetId: budget.id })
          navigate(navigation.expenses.budgets.detail.fullPath({ budgetId: budget.id }))
        }}
      >
        <Card.Eyebrow>
          <AvatarStack
            avatars={avatars}
            size="s48"
            leftover={leftover}
            backgroundColor="champagne1000"
            textSize="200"
            textColor="grey000White"
            overlap="s12"
          />
        </Card.Eyebrow>

        <Card.Title ellipsis lineClamp={2}>
          {budget.name}
        </Card.Title>

        <Card.Content>
          <Box width="full" height="full" flexDirection="columnReverse" gap="s32" paddingY="s12">
            <Box gap="s24" paddingTop="s4">
              <Box flexDirection="row" alignItems="center" gap="s4" justifyContent="spaceBetween">
                <Text.Normal size="200" weight="medium" capitalize>
                  {budgetPeriodicity()[budget.periodicity]}
                </Text.Normal>
                <Text.Normal size="200" weight="semibold">
                  {amountWithCurrency(
                    budget.amountCents ? Number(budget.amountCents) : 0,
                    budget.currency,
                    locale
                  )}
                </Text.Normal>
              </Box>

              <Box flexDirection="row" alignItems="center" gap="s4" justifyContent="spaceBetween">
                <Box flexDirection="row" alignItems="center" gap="s8">
                  <InfoDot color={progress.progressColor} size="medium" />
                  <Text.Normal size="200" weight="medium">
                    {i18n.t('budgets.detail.period_status.amount.reported')}
                  </Text.Normal>
                </Box>
                <Text.Normal size="200" weight="semibold">
                  {amountWithCurrency(
                    budget.spentAmountCents ? Number(budget.spentAmountCents) : 0,
                    budget.currency,
                    locale
                  )}
                </Text.Normal>
              </Box>

              <Box flexDirection="row" alignItems="center" gap="s4" justifyContent="spaceBetween">
                <Box flexDirection="row" alignItems="center" gap="s8">
                  <InfoDot color="grey400" size="medium" />
                  <Text.Normal size="200" weight="medium">
                    {overBudget
                      ? i18n.t('budgets.detail.period_status.amount.overspent')
                      : i18n.t('budgets.detail.period_status.amount.available')}
                  </Text.Normal>
                </Box>
                <Text.Normal
                  size="200"
                  weight="semibold"
                  color={overBudget ? 'radicalRed1200' : 'grey800'}
                >
                  {amountWithCurrency(Math.abs(availableAmount), budget.currency, locale)}
                </Text.Normal>
              </Box>
            </Box>
            <Box gap="s12" paddingTop="s16">
              <Text.Normal weight="semibold" color="grey1000">
                {statusText}
              </Text.Normal>
              <ProgressBar
                progressColor={progress.progressColor}
                barColor={budget.started ? progress.barColor : 'grey200'}
                amount={budget.spentAmountCents}
                total={budget.amountCents}
              />
            </Box>
          </Box>
        </Card.Content>
      </Card>
    </Box>
  )
}
